import React, { useState, useRef, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { products, categories } from "../data/products";
import { useCart } from "../context/CartContext";

gsap.registerPlugin(ScrollTrigger);

const ProductsSection = () => {
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [addedId, setAddedId] = useState(null);
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const filtered = products.filter((p) => {
    const matchCat = activeCategory === "all" || p.category === activeCategory;
    const matchSearch =
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.brand.toLowerCase().includes(search.toLowerCase()) ||
      p.type.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  useEffect(() => {
    gsap.fromTo(
      headingRef.current,
      { opacity: 0, y: 50 },
      {
        opacity: 1,
        y: 0,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 80%",
        },
      },
    );
  }, []);

  const handleAdd = (e, product) => {
    e.stopPropagation();
    addToCart(product);
    setAddedId(product.id);
    setTimeout(() => setAddedId(null), 1500);
  };

  return (
    <Section ref={sectionRef} id="products-section">
      <Inner>
        {/* Heading */}
        <HeadingBlock ref={headingRef}>
          <EyebrowRow>
            <EyebrowLine />
            <Eyebrow>Koleksioni Ynë</Eyebrow>
            <EyebrowLine />
          </EyebrowRow>
          <Heading>
            Zgjidh shijen <AccentWord>tënde.</AccentWord>
          </Heading>
        </HeadingBlock>

        {/* Search + Filter */}
        <Controls>
          <SearchWrapper>
            <SearchIcon>⌕</SearchIcon>
            <SearchInput
              placeholder="Kërko produkt, markë..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && <ClearBtn onClick={() => setSearch("")}>✕</ClearBtn>}
          </SearchWrapper>

          <FilterTabs>
            {categories.map((cat) => (
              <FilterTab
                key={cat.id}
                $active={activeCategory === cat.id}
                onClick={() => setActiveCategory(cat.id)}
              >
                {cat.label}
              </FilterTab>
            ))}
          </FilterTabs>
        </Controls>

        {/* Results count */}
        <ResultsRow>
          <ResultsCount>{filtered.length} produkte</ResultsCount>
        </ResultsRow>

        {/* Product Grid */}
        <Grid>
          {filtered.map((product) => (
            <Card
              key={product.id}
              onClick={() => navigate(`/product/${product.id}`)}
            >
              <CardTop>
                <ProductImg src={product.image} alt={product.name} />
                <BadgePill>{product.badge}</BadgePill>
                <ColorDot style={{ background: product.color }} />
              </CardTop>

              <CardBody>
                <ProductType>{product.type}</ProductType>
                <ProductName>{product.name}</ProductName>
                <ProductBrand>{product.brand}</ProductBrand>

                <MetaRow>
                  <Stars>
                    {"★".repeat(Math.floor(product.rating))}
                    {"☆".repeat(5 - Math.floor(product.rating))}
                  </Stars>
                  <ReviewCount>({product.reviews})</ReviewCount>
                  {product.origin && <Origin>{product.origin}</Origin>}
                </MetaRow>

                <CardFooter>
                  <Price>
                    €{product.price.toFixed(2)}
                    <PriceCurrency></PriceCurrency>
                  </Price>
                  <AddBtn
                    onClick={(e) => handleAdd(e, product)}
                    $added={addedId === product.id}
                  >
                    {addedId === product.id ? "✓" : "+"}
                  </AddBtn>
                </CardFooter>
              </CardBody>

              <CardHoverLine style={{ background: product.color }} />
            </Card>
          ))}
        </Grid>

        {filtered.length === 0 && (
          <Empty>
            <EmptyIcon>🔍</EmptyIcon>
            <EmptyText>Nuk u gjet asnjë produkt për "{search}"</EmptyText>
          </Empty>
        )}
      </Inner>
    </Section>
  );
};

export default ProductsSection;

// ─── Keyframes ────────────────────────────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pop = keyframes`
  0%   { transform: scale(1); }
  50%  { transform: scale(1.3); }
  100% { transform: scale(1); }
`;

// ─── Styled Components ────────────────────────────────────────────────────────

const Section = styled.section`
  position: relative;
  width: 100vw;
  min-height: 100vh;
  padding: 6rem 0 8rem;
  background: var(--bg);
  background: #140e0e;
  z-index: 9999;
`;

const Inner = styled.div`
  width: 100%;
  max-width: 1300px;
  margin: 0 auto;
  padding: 0 4rem;
  display: flex;
  flex-direction: column;
  gap: 3rem;
`;

const HeadingBlock = styled.div`
  opacity: 0;
`;

const EyebrowRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const EyebrowLine = styled.div`
  flex: 0 0 40px;
  height: 1px;
  background: var(--accent);
  opacity: 0.6;
`;

const Eyebrow = styled.span`
  font-family: var(--font-condensed);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: var(--accent);
`;

const Heading = styled.h2`
  font-family: var(--font-display);
  font-size: clamp(3rem, 6vw, 5.5rem);
  line-height: 0.92;
  color: var(--white);
  letter-spacing: -0.01em;
`;

const AccentWord = styled.span`
  color: var(--accent);
`;

/* Controls */
const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
`;

const SearchWrapper = styled.div`
  position: relative;
  flex: 1;
  min-width: 260px;
  max-width: 440px;
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 1.1rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1.2rem;
  color: var(--white-dim);
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(139, 58, 26, 0.25);
  border-radius: 999px;
  padding: 0.85rem 2.8rem 0.85rem 2.8rem;
  font-family: var(--font-body);
  font-size: 0.9rem;
  color: var(--white);
  outline: none;
  transition:
    border-color 0.25s ease,
    background 0.25s ease;

  &::placeholder {
    color: rgba(240, 236, 230, 0.3);
  }

  &:focus {
    border-color: var(--accent);
    background: rgba(139, 58, 26, 0.06);
  }
`;

const ClearBtn = styled.button`
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--white-dim);
  cursor: pointer;
  font-size: 0.8rem;
  padding: 0.2rem;
  transition: color 0.2s;

  &:hover {
    color: var(--white);
  }
`;

const FilterTabs = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const FilterTab = styled.button`
  font-family: var(--font-condensed);
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 0.6rem 1.4rem;
  border-radius: 999px;
  border: 1.5px solid
    ${({ $active }) => ($active ? "var(--accent)" : "rgba(139,58,26,0.25)")};
  background: ${({ $active }) => ($active ? "var(--accent)" : "transparent")};
  color: ${({ $active }) => ($active ? "var(--white)" : "var(--white-dim)")};
  cursor: pointer;
  transition: all 0.22s ease;

  &:hover {
    border-color: var(--accent);
    color: var(--white);
  }
`;

const ResultsRow = styled.div`
  display: flex;
  align-items: center;
  margin-top: -1rem;
`;

const ResultsCount = styled.span`
  font-family: var(--font-body);
  font-size: 0.8rem;
  font-weight: 300;
  color: rgba(240, 236, 230, 0.35);
  letter-spacing: 0.05em;
`;

/* Grid */
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
`;

const Card = styled.div`
  position: relative;
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(139, 58, 26, 0.12);
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  transition:
    transform 0.3s ease,
    border-color 0.3s ease,
    box-shadow 0.3s ease;
  animation: ${fadeUp} 0.5s ease both;

  &:hover {
    transform: translateY(-6px);
    border-color: rgba(139, 58, 26, 0.4);
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.4);
  }

  &:hover > div:last-child {
    transform: scaleX(1);
  }
`;

const CardTop = styled.div`
  position: relative;
  height: auto;
  overflow: hidden;
`;

const BadgePill = styled.span`
  position: absolute;
  top: 1rem;
  left: 1rem;
  font-family: var(--font-condensed);
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--accent);
  background: rgba(139, 58, 26, 0.12);
  border: 1px solid rgba(139, 58, 26, 0.3);
  border-radius: 999px;
  padding: 0.25rem 0.7rem;
`;

const ProductImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform 0.5s ease;

  ${Card}:hover & {
    transform: scale(1.07);
  }
`;

const ColorDot = styled.div`
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  opacity: 0.7;
`;

const CardBody = styled.div`
  padding: 1.4rem 1.6rem 1.6rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const ProductType = styled.span`
  font-family: var(--font-condensed);
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--accent);
  opacity: 0.8;
`;

const ProductName = styled.h3`
  font-family: var(--font-display);
  font-size: 1.3rem;
  color: var(--white);
  line-height: 1.1;
  letter-spacing: 0.01em;
`;

const ProductBrand = styled.p`
  font-family: var(--font-body);
  font-size: 0.78rem;
  font-weight: 300;
  color: var(--white-dim);
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.3rem;
  flex-wrap: wrap;
`;

const Stars = styled.span`
  font-size: 0.7rem;
  color: var(--accent);
  letter-spacing: 1px;
`;

const ReviewCount = styled.span`
  font-family: var(--font-body);
  font-size: 0.7rem;
  color: rgba(240, 236, 230, 0.35);
`;

const Origin = styled.span`
  font-family: var(--font-body);
  font-size: 0.65rem;
  color: rgba(240, 236, 230, 0.3);
  margin-left: auto;
  font-style: italic;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(139, 58, 26, 0.12);
`;

const Price = styled.span`
  font-family: var(--font-display);
  font-size: 1.5rem;
  color: var(--white);
  letter-spacing: 0.02em;
`;

const PriceCurrency = styled.span`
  font-family: var(--font-condensed);
  font-size: 0.75rem;
  color: var(--white-dim);
`;

const AddBtn = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 1.5px solid
    ${({ $added }) => ($added ? "var(--accent)" : "rgba(139,58,26,0.4)")};
  background: ${({ $added }) => ($added ? "var(--accent)" : "transparent")};
  color: var(--white);
  font-size: 1.2rem;
  font-weight: 300;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.22s ease;
  animation: ${({ $added }) => ($added ? pop : "none")} 0.3s ease;

  &:hover {
    background: var(--accent);
    border-color: var(--accent);
    transform: scale(1.1);
  }
`;

const CardHoverLine = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.4s ease;
`;

/* Empty state */
const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 5rem 0;
`;

const EmptyIcon = styled.span`
  font-size: 3rem;
  opacity: 0.3;
`;

const EmptyText = styled.p`
  font-family: var(--font-body);
  font-size: 1rem;
  font-weight: 300;
  color: var(--white-dim);
`;
